import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeftIcon, CloudArrowUpIcon, PlusIcon, TrashIcon, ArrowUpCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import {
  fetchAppDetails,
  fetchTenantAppDetails,
  getPublicAppVersionHistory,
  getTenantAppVersions,
  listDeployments,
  deleteTenantApp,
  downloadAppPackage,
} from '../services/api'
import type { Deployment, PublicAppVersionHistory } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { Badge, SectionCard, EmptyState, Modal } from '../components/ui'
import DeployWizard from '../components/DeployWizard'
import AddInstallerModal from '../components/AddInstallerModal'
import type { WizardTarget } from '../components/DeployWizard'
import { deploymentTitle, groupDeploymentRows, type DeploymentRow } from '../utils/deploymentRows'
import DeploymentStatusBadge from '../components/DeploymentStatusBadge'
import MarkdownDescription from '../components/MarkdownDescription'
import { sourceTypeLabel, sourceTypeTitle } from '../utils/sourceType'
import { errorText } from '../utils/errorText'
import { notInCatalogLabel, notInCatalogPhrase } from '../utils/catalogEntry'

/**
 * One package in the catalog — rebuilt to the partner mock
 * (sap-mockup/app/(console)/apps/[id]/page.tsx).
 *
 * The catalog holds the CLEAN package: what was built, before any customer settings. Everything
 * that varies per rollout lives on a DEPLOYMENT, and this page links to them rather than
 * restating them — the same package can follow new versions on one rollout and stay pinned on
 * another, so a single per-app answer was never right (spec §11.3a, PR #6).
 *
 * This replaced a 2,145-line page. What came back, and what deliberately did not:
 *
 *   DOWNLOADS live on the version row, beside the version they were built from. An Advanced
 *   section briefly carried them alongside install/uninstall commands, detection rules and
 *   package metadata, and was dropped: none of it was information the page did not already
 *   convey, and a fold nobody opens is worse than no fold.
 *
 *   STILL OUT, because they are facts about a ROLLOUT, not the package — one package can follow
 *   new versions on one deployment and stay pinned on another, so a single answer here would be
 *   wrong for every rollout but one: Subscription & Sync · Auto-Update · Assignment editing ·
 *   deploy-time overrides (now the deploy wizard's "Adjust"). They live on the deployment detail
 *   page, which each row in the Deployments list links to.
 *
 *   NOT PORTED: Package Files browser, Packaging Report, Bundle Deployment, Edit metadata.
 *
 * The previous version is in git history at 955baff; the Advanced section at c1d72be.
 */

/**
 * Anything that has been PACKAGED can be deployed — deploying is what produces the .intunewin, so
 * requiring one first had it backwards (the mock's note at page.tsx:75-80, which still gates on
 * `installMethods.length > 0`). Our equivalent of "has an install method" is a non-empty
 * package_storage. Absent field => allow, so an older record without one does not lose its button.
 */
/**
 * Which of the two package forms (PSADT package, .intunewin) the packaging run failed to
 * produce, as the server reports it — the same verdict its deploy endpoints enforce. Only a
 * tenant app's response carries the field; without it the page does not judge, and the server's
 * refusal is the backstop.
 */
function missingPackageMethods(app: any): string[] {
  return Array.isArray(app?.missing_package_methods) ? app.missing_package_methods : []
}

function canDeployApp(app: any): boolean {
  if (!app) return false
  // An incomplete package is refused by the server; offering the button would only collect
  // that refusal. A test failure alone is a warning, not a block.
  if (missingPackageMethods(app).length > 0) return false
  const ps = app.package_storage
  if (ps && typeof ps === 'object') return Object.keys(ps).length > 0
  return true
}

export default function AppDetail() {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuthStore()
  const [deployOpen, setDeployOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  /** The deployment the wizard opens on: the one an Update button belongs to, or a new one. */
  const [deployTarget, setDeployTarget] = useState<WizardTarget | undefined>(undefined)
  const openDeploy = (target?: WizardTarget) => {
    setDeployTarget(target)
    setDeployOpen(true)
  }
  const [showAllVersions, setShowAllVersions] = useState(false)
  const downloadState = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const isTenantApp = !!appId?.startsWith('tenant-')
  const isPublicCatalogApp = !isTenantApp

  const { data: tenantApp } = useQuery({
    queryKey: ['tenant-app', appId],
    queryFn: () => fetchTenantAppDetails(appId!),
    enabled: !!appId && isTenantApp,
    retry: false,
  })
  const { data: centralApp, isLoading } = useQuery({
    queryKey: ['app', appId],
    queryFn: () => fetchAppDetails(appId!),
    enabled: !!appId && isPublicCatalogApp,
    retry: false,
  })
  const app: any = tenantApp || centralApp

  const deploymentsQ = useIntuneQuery({
    queryKey: intuneKeys.deployments(),
    queryFn: listDeployments,
    enabled: !!appId,
  })

  const versionsQ = useQuery({
    queryKey: ['app-versions', appId],
    // Two different endpoints with two different response types; both carry `versions[]`, which
    // is all this page reads, so the union is narrowed where it is consumed rather than here.
    queryFn: async (): Promise<any> =>
      isPublicCatalogApp
        ? getPublicAppVersionHistory(appId!)
        : getTenantAppVersions(appId!, { limit: 50 }),
    enabled: !!appId,
    retry: false,
  })

  if (isLoading && !app) {
    return <p className="py-24 text-center text-theme-text-tertiary">Loading…</p>
  }
  if (!app) {
    return (
      <div className="py-24 text-center">
        <p className="text-theme-text-tertiary">Package not found.</p>
        <Link to="/" className="mt-2 inline-block text-theme-accent-blue hover:underline">
          Back to catalog
        </Link>
      </div>
    )
  }

  const name = app.display_name || app.name
  const version = app.current_version?.version || app.version || ''
  // The mock reads a flat `releaseNotes`; ours has hung it off current_version historically, and
  // some records carry it flat. Read both rather than showing an empty card for half the catalog.
  const releaseNotes = app.current_version?.release_notes || app.release_notes
  // Keyed on the app's OWN visibility, not on which route it came in through. A public-catalog
  // entry that is not certified must not be badged Certified just because it is not private —
  // and a tenant upload ('tenant_private') is private, not Certified.
  const isPrivateApp = app.visibility
    ? app.visibility === 'private' || app.visibility === 'tenant_private'
    : isTenantApp
  // Certification is the packaging run's own verdict: a package whose tests failed says so
  // instead, wherever the page would otherwise vouch for it.
  // A public catalog app comes from central-backend, which does not record what it was built from;
  // the version history (tenant-backend) does.
  const sourced = {
    source_type: app.source_type ?? versionsQ.data?.source_type,
    source_reference: app.source_reference,
  }
  const sourceTag = sourceTypeLabel(sourced)
  const testsFailed = !!app.warning_message
  const packageIncomplete = missingPackageMethods(app).length > 0

  // Deployments of THIS package. The list endpoint is tenant-wide, so the filter happens here.
  // A tenant app's route id carries a `tenant-` prefix that the deployment row does not, hence
  // the second comparison — matching on name as well would risk pulling in a different package
  // that happens to share a display name.
  const bareId = appId?.replace(/^tenant-/, '')
  const deployments: Deployment[] = (deploymentsQ.data?.deployments ?? []).filter(
    (d) => d.app_id && (d.app_id === appId || d.app_id === bareId),
  )
  /** One item per deployment, the package's first deployment (no uid) first. */
  const deploymentRows = groupDeploymentRows(deployments).sort((a, b) =>
    !a.primary.deployment_uid ? -1 : !b.primary.deployment_uid ? 1 : 0,
  )

  const versionRows: {
    version: string
    date: string | null
    id: string | null
    hasPackage: boolean
    status: string
  }[] = (() => {
    const v: any = versionsQ.data
    const rows = (v?.versions ?? []).map((r: any) => ({
      version: r.version,
      date: r.published_at || r.created_at || null,
      id: r.id ?? null,
      hasPackage: !!r.has_package,
      status: String(r.status ?? '').toLowerCase(),
    }))
    // No client-side fallback for "the current version is missing from the list": the API
    // guarantees it (api/apps.py, both version-history endpoints). Re-adding it here would give
    // two places that decide what the catalog holds, and they would drift.
    return rows
  })()
  const shownVersions = showAllVersions ? versionRows : versionRows.slice(0, 3)
  // The mock's `pendingVersion`: a version sitting in the catalog that this app is not on.
  // Safe to read off the head of the list — the server orders these `created_at.desc()`
  // (api/apps.py:898), so newest-first is guaranteed rather than assumed.
  // A failed attempt is history, not a version waiting to be adopted.
  const newestUsable = versionRows.find((r) => r.status !== 'failed')
  const pendingVersion =
    newestUsable && version && newestUsable.version !== version ? newestUsable.version : null

  // What the catalog can hand over. NOTE this is the CLEAN build: the customer install command
  // and branding are baked on at PUBLISH (-DeploymentConfig b64:), so this file is what was
  // packaged, not what a given rollout shipped. The mock moves download onto the deployment for
  // exactly that reason (spec §11.3a); until a deployment page exists this is its only home, so
  // the wording says which one it is rather than pretending.
  const methods: any[] = app.current_version?.deployment_methods ?? []
  const [downloading, setDownloading] = downloadState

  const download = async (method: string, fileName?: string, versionId?: string | null) => {
    setDownloading(versionId ? `${versionId}:${method}` : method)
    try {
      // Each click asks for a new link, so a download that failed after its link expired is
      // started again by clicking again.
      await downloadAppPackage(
        appId!, method, versionId,
        fileName || `${name}-${method}.${method === 'intune' ? 'intunewin' : 'zip'}`,
      )
    } catch {
      // The blob endpoint has no useful JSON body to surface; the button simply re-enables.
    } finally {
      setDownloading(null)
    }
  }

  // A catalog entry that left the catalog (superseded or removed) opens read-only: its existing
  // deployments can be updated and removed, but no new deployment of it is made, and the page
  // links to the package's current entry. The server refuses a new deployment of it as well.
  const catalogEntry: PublicAppVersionHistory['catalog_entry'] = isPublicCatalogApp
    ? versionsQ.data?.catalog_entry
    : undefined
  const leftCatalog = !!catalogEntry && !catalogEntry.deployable
  const replacement = leftCatalog ? catalogEntry.replaced_by : null
  const newDeploymentBlocked = !leftCatalog
    ? undefined
    : replacement
      ? `This catalog entry was replaced by ${replacement.name} ${replacement.version}, so no new deployment of it can be made. Deploy the current entry instead.`
      : `This catalog entry ${notInCatalogPhrase(catalogEntry?.status)}, so no new deployment of it can be made.`

  // A Viewer is read-only: the server refuses the deployment, so the button is not offered.
  const canUpdate = canDeployApp(app) && hasPermission('deploy_apps')
  // A catalog entry's standing comes with its version history; until that read settles, a new
  // deployment is not offered, so the button never shows on an entry that cannot take one.
  const entryKnown = !isPublicCatalogApp || versionsQ.isSuccess || versionsQ.isError
  const canDeploy = canUpdate && entryKnown && !leftCatalog
  // Same gate the catalog uses: only PRIVATE apps, only with manage_apps. A public catalog entry
  // is shared with every tenant, so deleting one here would be deleting someone else's app.
  const canDelete = isTenantApp && hasPermission('manage_apps')
  // Upload new version: an uploaded app, for the roles that may package (the server checks both).
  const canUploadNewVersion = !!app.can_upload_new_version && hasPermission('deploy_apps')

  const doDelete = async () => {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteTenantApp(appId!)
      // Land on the catalog. Staying on the detail page of something that no longer exists would
      // show "Package not found", which reads as a failure rather than as the delete working.
      queryClient.invalidateQueries({ queryKey: ['tenant-apps'] })
      queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
      navigate('/')
    } catch (e: any) {
      setDeleteError(errorText(e, 'Could not delete the package.'))
      setDeleting(false)
    }
  }

  /** A new version reaches only the package's first deployment on its own: always for a private
   *  app, and for a catalog package when a row says another deployment is not updated
   *  automatically. */
  const firstDeploymentOnly =
    isTenantApp ||
    deploymentRows.some((r) => r.primary.deployment_uid && r.primary.updated_automatically === false)

  /** One deployment: its name links to its page, its Intune apps are listed inside it. */
  const deploymentItem = (row: DeploymentRow) => {
    const d = row.primary
    const uid = d.deployment_uid ?? null
    const title = deploymentTitle(name, d.deployment_name)
    return (
      <div
        key={row.key}
        data-deployment={uid ?? ''}
        className="rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link to={`/deployments/${d.id}`} className="group min-w-0">
            <p className="truncate text-[13px] font-semibold text-theme-text-primary group-hover:underline">
              {title}
            </p>
            <p className="mt-0.5 truncate text-[12px] text-theme-text-secondary">
              {d.target_details || d.target_type}
              {d.device_count != null &&
                ` · ${d.device_count} device${d.device_count === 1 ? '' : 's'}`}
            </p>
          </Link>
          <span className="flex shrink-0 items-center gap-2">
            {d.version && (
              <span className="font-mono text-[12px] text-theme-text-tertiary">{d.version}</span>
            )}
            <DeploymentStatusBadge status={row.status} fallback={d.status} />
            {canUpdate && (
              <button
                type="button"
                onClick={() => openDeploy({ action: 'update', uid })}
                aria-label={`Update ${title}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-2.5 py-1 text-[12px] text-theme-text-primary hover:bg-theme-secondary"
              >
                <CloudArrowUpIcon className="h-3.5 w-3.5" /> Update
              </button>
            )}
          </span>
        </div>
        {/* A deployment no automatic update reaches gets a newer version when it is updated. */}
        {row.apps.some((a) => a.updated_automatically === false && a.sync_pending) && (
          <p className="mt-2 text-[12px] text-theme-text-secondary">
            A newer version is available. Update this deployment to deploy it.
          </p>
        )}
        <ul className="mt-2 space-y-0.5" aria-label={`Intune apps of ${title}`}>
          {row.apps.map((a) =>
            a.failed_publish ? (
              // Linked to its own page, where Remove deletes only this app.
              <li key={a.id} className="truncate text-[12px] text-red-600 dark:text-red-300">
                <Link to={`/deployments/${a.id}`} className="hover:underline">
                  Failed publish, left in Intune; remove it
                </Link>
                {' · '}
                <span className="font-mono">{a.app_name}</span>
                {a.version ? ` · ${a.version}` : ''}
              </li>
            ) : (
              <li key={a.id} className="truncate text-[12px] text-theme-text-tertiary">
                {a.removal_incomplete
                  ? 'Older version, not removed'
                  : a.role === 'update_only'
                    ? 'Update-only app'
                    : 'Install app'}
                {' · '}
                <span className="font-mono">{a.app_name}</span>
                {a.version ? ` · ${a.version}` : ''}
              </li>
            ),
          )}
        </ul>
      </div>
    )
  }

  const deployBtn = (
    <button
      type="button"
      onClick={() => openDeploy()}
      className="inline-flex items-center gap-2 rounded-lg bg-theme-accent-blue px-3 py-2 text-sm font-medium text-white hover:opacity-90"
    >
      <CloudArrowUpIcon className="h-4 w-4" /> Deploy to Intune
    </button>
  )

  /**
   * Deploying is an ACTION taken against the app on this page, so the wizard opens OVER it and
   * this page stays where it was — the reference mounts it the same way
   * (sap-mockup/app/(console)/apps/[id]/page.tsx:225). It used to take over the content area
   * behind a breadcrumb, which read as navigating away from the app you were looking at.
   */
  const deployWizard = deployOpen ? (
    <DeployWizard
      isOpen
      onClose={() => setDeployOpen(false)}
      initialTarget={deployTarget}
      apps={[
        {
          id: appId!,
          name,
          kind: isPublicCatalogApp ? 'public' : 'private',
          // Lets the wizard resolve {Version}/{Publisher}/{Arch} into real names.
          publisher: app.publisher,
          version,
          // See AppStore: the central catalog API carries the architecture inside
          // current_version, with no top-level field.
          arch: app.architecture || app.current_version?.architecture,
          newDeploymentBlocked,
          notFullyTested: !!app.not_fully_tested,
        },
      ]}
      // The wizard has already closed by the time either of these fires. The rollout list is
      // read twice for that reason: once when the job is recorded, so the deployment shows up
      // here as pending, and again when it finishes.
      onStarted={() => {
        deploymentsQ.refetch()
      }}
      onDeployed={() => {
        deploymentsQ.refetch()
      }}
    />
  ) : null

  return (
    <div className="mx-auto max-w-4xl">
      {deployWizard}
      <Link
        to="/"
        className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-theme-text-tertiary hover:text-theme-text-primary"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Back
      </Link>

      <div className="mb-6 flex flex-wrap items-start gap-4">
        {app.icon_url ? (
          <img
            src={app.icon_url}
            alt=""
            className="h-16 w-16 shrink-0 rounded-xl object-contain"
          />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-theme-tertiary text-xl font-semibold text-theme-text-tertiary">
            {name?.[0] ?? '?'}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-theme-text-primary">{name}</h1>
            {leftCatalog ? (
              <Badge tone="slate">{notInCatalogLabel(catalogEntry?.status)}</Badge>
            ) : packageIncomplete ? (
              <Badge tone="red">Incomplete package</Badge>
            ) : testsFailed ? (
              <Badge tone="amber">Tests failed</Badge>
            ) : isPrivateApp ? (
              <Badge tone="violet">Private</Badge>
            ) : (
              <Badge tone="green">Certified</Badge>
            )}
            {sourceTag && (
              <span title={sourceTypeTitle(sourced)}>
                <Badge tone="slate">{sourceTag}</Badge>
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-theme-text-secondary">
            {[app.publisher, version && `v${version}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDelete && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              title="Delete package"
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-3 py-2 text-sm text-theme-text-secondary hover:border-red-500/40 hover:text-red-500"
            >
              <TrashIcon className="h-4 w-4" /> Delete
            </button>
          )}
          {canUploadNewVersion && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-3 py-2 text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              <CloudArrowUpIcon className="h-4 w-4" /> Upload new version
            </button>
          )}
          {canDeploy && deployBtn}
        </div>
      </div>
      {canUploadNewVersion && (
        <AddInstallerModal
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          targetApp={{ id: appId!, name: app.display_name || app.name, version: app.current_version?.version }}
        />
      )}

      {leftCatalog && (
        <div
          role="status"
          className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-300"
        >
          {replacement ? (
            <>
              <span className="font-semibold">
                This catalog entry was replaced by{' '}
                <Link to={`/apps/${replacement.id}`} className="underline">
                  {replacement.name} {replacement.version}
                </Link>
                .
              </span>{' '}
            </>
          ) : (
            <span className="font-semibold">
              This catalog entry {notInCatalogPhrase(catalogEntry?.status)}.{' '}
            </span>
          )}
          It cannot be deployed again. Its existing deployments can still be updated or removed.
        </div>
      )}

      {app.repackage?.status === 'failed' && (
        <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-300">
          <span className="font-semibold">The newest packaging job failed.</span>{' '}
          {app.repackage.error_message ? `${app.repackage.error_message} ` : ''}
          The published package is unchanged and can still be deployed.{' '}
          <Link to="/jobs" className="underline">View jobs</Link>
        </div>
      )}

      {packageIncomplete ? (
        <div className="mb-6 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          <span className="font-semibold">Incomplete package.</span>{' '}
          The packaging run did not produce both a PSADT package and an .intunewin, so deploying
          to Intune is blocked. Repackage the app.
        </div>
      ) : app.not_fully_tested ? (
        <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-300">
          <span className="font-semibold">Not fully tested.</span>{' '}
          This package was published anyway: {String(app.warning_message || '').replace(/^Not fully tested: /, '')}{' '}
          It is never deployed automatically.
        </div>
      ) : testsFailed ? (
        <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-300">
          <span className="font-semibold">Packaging tests failed.</span>{' '}
          {app.warning_message} Review the report before deploying to Intune.
        </div>
      ) : null}

      {app.description && (
        <SectionCard title="Description" className="mb-6">
          <MarkdownDescription content={app.description} />
        </SectionCard>
      )}

      {/* ---- deployments of this package ---------------------------------- */}
      <SectionCard
        title="Deployments"
        className="mb-6"
        action={
          canDeploy ? (
            <button
              type="button"
              onClick={() => openDeploy({ action: 'new' })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-[13px] text-theme-text-primary hover:bg-theme-tertiary"
            >
              <PlusIcon className="h-3.5 w-3.5" /> New deployment
            </button>
          ) : undefined
        }
      >
        {deploymentsQ.isLoading ? (
          <p className="py-4 text-sm text-theme-text-tertiary">Loading…</p>
        ) : deployments.length === 0 ? (
          <EmptyState
            title="Not deployed yet"
            hint={
              newDeploymentBlocked
                ? newDeploymentBlocked
                : packageIncomplete
                ? 'The packaging run did not produce a complete package, so deploying to Intune is blocked. Repackage the app first.'
                : 'Deploying publishes this package to Intune with a user experience and a set of assignments.'
            }
            action={canDeploy ? deployBtn : undefined}
          />
        ) : (
          <>
            <div className="space-y-2">{deploymentRows.map(deploymentItem)}</div>
            <p className="mt-3 text-[12px] text-theme-text-tertiary">
              Each deployment has its own Intune apps, assignments and install context. Update
              applies the chosen templates to that deployment and creates no second Intune app; a
              template that adds an install app or an update-only app adds it to that deployment.
              New deployment publishes another set of apps, whose app and generated group names
              follow the chosen templates; the deployment's name is added to a name only when that
              name is already in use. A deployment's install context is not changed in place:
              remove it and deploy again, or create a new deployment.{' '}
              {firstDeploymentOnly
                ? 'Each deployment has its own update policy, but a new version of this package is deployed automatically to its first deployment only; update the other deployments to deploy it.'
                : 'Each deployment follows its own update policy.'}
            </p>
          </>
        )}
      </SectionCard>

      {/* ---- what the catalog holds --------------------------------------- */}
      <SectionCard title="Versions in the catalog" className="mb-6">
        {versionRows.length === 0 ? (
          <p className="py-2 text-sm text-theme-text-tertiary">No version history.</p>
        ) : (
          <>
            <ul className="divide-y divide-theme-border-primary">
              {shownVersions.map((v) => (
                <li
                  key={v.version}
                  className="flex items-center justify-between gap-4 py-2 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-mono text-[13px] text-theme-text-primary">
                      {v.version}
                    </span>
                    {v.version === version && <Badge tone="green">Current</Badge>}
                    {v.version !== version && v.status === 'packaging' && (
                      <Badge tone="amber">In preparation</Badge>
                    )}
                    {v.version !== version && v.status === 'superseded' && (
                      <Badge tone="slate">Superseded</Badge>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    {/* Download belongs beside the version it shipped, which is why it sits here
                        rather than in Advanced. The current version serves the app's package;
                        an older version serves the folder its history entry points at, when
                        that folder is still on disk (has_package). */}
                    {(v.version === version
                      ? methods
                      : v.hasPackage
                        ? [{ method: 'intune' }, { method: 'psadt' }]
                        : []
                    ).map((m: any) => (
                        <button
                          key={m.method}
                          type="button"
                          disabled={
                            downloading ===
                            (v.version === version ? m.method : `${v.id}:${m.method}`)
                          }
                          onClick={() =>
                            download(
                              m.method,
                              v.version === version ? m.package_file : undefined,
                              v.version === version ? null : v.id,
                            )
                          }
                          title={
                            m.method === 'intune'
                              ? 'Download the .intunewin'
                              : 'Download the PSADT package'
                          }
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-theme-accent-blue hover:underline disabled:opacity-50"
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          {downloading ===
                          (v.version === version ? m.method : `${v.id}:${m.method}`)
                            ? 'Preparing…'
                            : m.method === 'intune'
                              ? '.intunewin'
                              : 'PSADT'}
                        </button>
                      ))}
                    <span className="text-[12px] text-theme-text-secondary">
                      {v.date ? new Date(v.date).toLocaleDateString() : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {versionRows.length > 3 && (
              <button
                type="button"
                onClick={() => setShowAllVersions((s) => !s)}
                className="mt-2 text-[12px] font-medium text-theme-accent-blue hover:underline"
              >
                {showAllVersions
                  ? 'Show fewer'
                  : `Show ${versionRows.length - 3} older versions`}
              </button>
            )}
            {pendingVersion && (
              <p className="mt-3 flex items-start gap-2 rounded-lg border border-theme-accent-blue/30 bg-theme-accent-blue/[0.08] px-3 py-2 text-[12px] text-theme-text-primary">
                <ArrowUpCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-theme-accent-blue" />
                <span>
                  <span className="font-mono">{pendingVersion}</span> is available. What happens to
                  it is a per-deployment setting — a rollout either follows new versions or stays
                  where it is.
                </span>
              </p>
            )}
          </>
        )}
      </SectionCard>

      {releaseNotes && (
        <SectionCard title="Release Notes" className="mb-6">
          <p className="whitespace-pre-wrap font-mono text-[13px] text-theme-text-secondary">
            {releaseNotes}
          </p>
        </SectionCard>
      )}

      <Modal
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        title="Delete package"
        footer={
          <>
            {deleteError && <p className="mr-auto text-sm text-red-500">{deleteError}</p>}
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-sm text-theme-text-primary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={doDelete}
              disabled={deleting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        <p className="text-sm text-theme-text-secondary">
          Delete <strong className="text-theme-text-primary">{name}</strong> and its package from
          this tenant? This cannot be undone.
        </p>
        {deploymentRows.length > 0 && (
          <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[13px] text-theme-text-secondary">
            {deploymentRows.length === 1 ? 'One deployment of' : `${deploymentRows.length} deployments of`} this
            package {deploymentRows.length === 1 ? 'is' : 'are'} still published to Intune. Deleting
            the app here does not remove {deploymentRows.length === 1 ? 'it' : 'them'} from Intune.
          </p>
        )}
      </Modal>
    </div>
  )
}
